/**
 * Approvals hub — single entry point for everything that needs an approver's
 * eyes. Top-level tabs:
 *   - ใบลา / OT          (all approver roles: SUPERVISOR + HR + OWNER)
 *   - ลาฉุกเฉิน          (HR/OWNER)
 *   - คำขอแก้ข้อมูล      (HR/OWNER)
 *   - ร้องเรียน / Tickets (HR/OWNER)
 *
 * Each tab renders a content-only component lifted from the former
 * /hr/emergency-leave, /hr/pending, /hr/tickets pages. URL hash
 * (#leave-ot|emergency|changes|tickets) preserves deep links.
 */
import { useEffect, useState } from 'react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { HubHeader, PillTabs, roleLabelTh } from '../components/layout/HubHeader';
import { usePendingApprovals, useActOnApproval, useMenuBadgeCounts, useUpcomingApproved, useRequestCancelApproved, useDecideShiftChange, usePendingAttendanceCorrections, useDecideAttendanceCorrection, useShiftOptions, useApprovalHistory, type ApprovalHistoryItem, type TimelineStep } from '../hooks';
import { resolveFileUrl } from '../lib/api-client';
import { Modal } from '../components/ui/Modal';
import { useToast } from '../components/ui/toast';
import { ComplianceAlertList, type ComplianceItem } from '../components/ui/ComplianceAlert';
import { EmptyState } from '../components/ui/EmptyState';
import { useAuthStore } from '../lib/store';
import { HREmergencyLeaveContent } from './hr/EmergencyLeave';
import { HRPendingContent } from './hr/Pending';
import { HRTicketsContent } from './hr/Tickets';
import { Check, X, MessageCircle, ChevronDown, ChevronUp, AlertTriangle, Paperclip, CheckCircle2, ArrowRight } from 'lucide-react';

type TopTab = 'leave-ot' | 'upcoming' | 'emergency' | 'changes' | 'tickets' | 'history';
const VALID_TOP_TABS: TopTab[] = ['leave-ot', 'upcoming', 'emergency', 'changes', 'tickets', 'history'];

interface PendingLeave {
  leave_id: string;
  request_group_id?: string | null;
  emp_code: string;
  emp_name?: string;
  date: string;
  leave_type: string;
  reason: string | null;
  current_level: number;
  is_backdated: boolean;
  is_emergency?: boolean;
  period?: 'full' | 'morning' | 'afternoon' | 'custom';
  start_time?: string | null;
  end_time?: string | null;
  evidence_url?: string | null;
  submitted_at?: string;
  approval_timeline?: TimelineStep[];
}

interface PendingOT {
  ot_id: string;
  emp_code: string;
  emp_name?: string;
  date: string;
  ot_type: string;
  start_time: string;
  end_time: string;
  reason: string;
  current_level: number;
  submitted_at?: string;
  approval_timeline?: TimelineStep[];
}

interface PendingShiftChange {
  id: string;
  emp_code: string;
  emp_name?: string;
  request_type?: 'day_off_move' | 'shift_change';
  original_date: string;
  target_date: string;
  original_shift_id?: string | null;
  target_shift_id?: string | null;
  reason: string | null;
  is_urgent?: boolean;
  current_level?: number;
  required_levels?: number;
  submitted_at?: string;
}

interface Pending {
  leaves: PendingLeave[];
  ots: PendingOT[];
  shift_changes: PendingShiftChange[];
}

const LEAVE_TYPE_LABEL: Record<string, string> = {
  sick: 'ลาป่วย',
  personal: 'ลากิจ',
  vacation: 'ลาพักร้อน',
  unpaid: 'ลาไม่รับเงิน',
  maternity: 'ลาคลอด',
};

const PERIOD_LABEL: Record<string, string> = {
  full: 'เต็มวัน',
  morning: 'ครึ่งวันเช้า',
  afternoon: 'ครึ่งวันบ่าย',
  custom: 'ระบุเวลาเอง',
};

/** วันเวลาแบบย่อ dd/mm HH:MM (เขตเวลาไทย) สำหรับ trail การอนุมัติ */
function fmtTrailTime(iso: string): string {
  return new Intl.DateTimeFormat('th-TH', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Bangkok',
  }).format(new Date(iso));
}

/**
 * Trail การอนุมัติแต่ละชั้น — ให้ผู้อนุมัติเห็นว่าชั้นก่อนหน้าใครกดผ่านแล้ว เวลาเท่าไหร่
 * โชว์เฉพาะใบที่มีมากกว่า 1 ชั้น (ใบชั้นเดียวไม่มี trail ให้ดู).
 */
function ApprovalTrail({ timeline }: { timeline?: TimelineStep[] }) {
  if (!timeline || timeline.length <= 1) return null;
  return (
    <div className="mb-2 rounded-lg bg-gray-50 px-2.5 py-2 space-y-1">
      {timeline.map((t) => {
        const done = !!t.decided_at && t.decision === 'approved';
        return (
          <div key={t.level} className="flex items-center gap-1.5 text-xs">
            {done ? (
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            ) : (
              <span className="w-3.5 h-3.5 rounded-full border border-gray-300 shrink-0" />
            )}
            <span className="text-gray-500 shrink-0">ชั้น {t.level}</span>
            {done ? (
              <>
                <span className="text-gray-700 truncate">{t.approver_name ?? '—'}</span>
                <span className="text-gray-400 ml-auto whitespace-nowrap">{fmtTrailTime(t.decided_at!)}</span>
              </>
            ) : (
              <span className="text-amber-600 ml-auto whitespace-nowrap">⏳ รออนุมัติ</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * ข้อควรตรวจก่อนอนุมัติใบลา (Non-HR UX §4) — ช่วยผู้อนุมัติที่ไม่ใช่ HR ไม่ให้
 * อนุมัติผ่านสิ่งที่ควรขอหลักฐาน/ตรวจก่อน. เบาๆ inline — ไม่บล็อกปุ่มอนุมัติ
 * (อนุมัติลาเป็น action ที่ทำบ่อย ต้องคงคลิกเดียว — frictionless).
 */
function leaveAlerts(l: PendingLeave): ComplianceItem[] {
  const items: ComplianceItem[] = [];
  if (l.leave_type === 'sick' && !l.evidence_url) {
    items.push({
      level: 'warn',
      title: 'ลาป่วยไม่มีใบรับรองแพทย์แนบ',
      detail: 'ลาป่วยตั้งแต่ 3 วันทำงานขึ้นไป นายจ้างขอใบรับรองแพทย์ได้ — ถ้าเข้าเงื่อนไขควรขอหลักฐานก่อนอนุมัติ',
      cite: 'พ.ร.บ.คุ้มครองแรงงาน ม.32',
    });
  }
  if (l.is_backdated) {
    items.push({
      level: 'warn',
      title: 'ลาย้อนหลัง',
      detail: 'คำขอนี้ลงวันที่ย้อนหลัง — ตรวจว่ามีเหตุผลรองรับก่อนอนุมัติ',
    });
  }
  return items;
}

/** ใบลาหลายวัน = หลายแถว (ราย 1 วัน) ใช้ request_group_id เดียวกัน — ยุบเป็น "ใบเดียว"
 *  ให้ผู้อนุมัติเห็น 1 การ์ด/ช่วงวัน + กดครั้งเดียว (backend อนุมัติทั้งกลุ่มพร้อมกัน). */
interface GroupedLeave extends PendingLeave {
  dates: string[];
  days_count: number;
  date_label: string;
}

function groupPendingLeaves(leaves: PendingLeave[]): GroupedLeave[] {
  const groups = new Map<string, PendingLeave[]>();
  for (const l of leaves) {
    const key = l.request_group_id ?? l.leave_id;
    const arr = groups.get(key) ?? [];
    arr.push(l);
    groups.set(key, arr);
  }
  return [...groups.values()].map((rows) => {
    const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
    const rep = sorted[0]!;
    const dates = sorted.map((r) => r.date);
    const first = dates[0]!;
    const last = dates[dates.length - 1]!;
    return {
      ...rep,
      // ธงเตือน/หลักฐานถือเป็นของทั้งใบ: วันไหนในใบเข้าเงื่อนไข = ทั้งใบติดธง
      is_backdated: sorted.some((r) => r.is_backdated),
      is_emergency: sorted.some((r) => r.is_emergency),
      evidence_url: rep.evidence_url ?? sorted.find((r) => r.evidence_url)?.evidence_url ?? null,
      dates,
      days_count: dates.length,
      date_label: dates.length === 1 ? first : `${first} – ${last} (${dates.length} วัน)`,
    };
  });
}

/** Inner content for the "ใบลา / OT" tab (was the standalone Approvals page). */
function LeaveOtApprovalsContent() {
  const { data, isLoading } = usePendingApprovals() as { data: Pending | undefined; isLoading: boolean };
  const act = useActOnApproval();
  const decideChange = useDecideShiftChange();
  const { data: shiftOptions } = useShiftOptions();
  const shiftNameOf = (id?: string | null) => {
    const s = (shiftOptions ?? []).find((x) => x.id === id);
    return s ? `${s.name} (${s.start_time}–${s.end_time})` : null;
  };
  const myRole = useAuthStore((s) => s.profile?.role ?? 'EMPLOYEE');
  const canCertify = myRole === 'HR' || myRole === 'OWNER' || myRole === 'MANAGER';
  const { data: corrections } = usePendingAttendanceCorrections(canCertify);
  const decideCorrection = useDecideAttendanceCorrection();
  const [tab, setTab] = useState<'leave' | 'ot' | 'shift_change' | 'attendance_correction'>('leave');
  const [expanded, setExpanded] = useState<string | null>(null);
  const toast = useToast();
  // Reason modal — used by both "reject" and "request more info".
  const [reasonModal, setReasonModal] = useState<{ kind: 'leave' | 'ot'; id: string; action: 'reject' | 'request_info' } | null>(null);
  const [reasonText, setReasonText] = useState('');
  // Reason modal สำหรับ "ไม่อนุมัติ" คำขอย้ายวันหยุด
  const [changeRejectId, setChangeRejectId] = useState<string | null>(null);
  const [changeRejectReason, setChangeRejectReason] = useState('');
  // Reason modal สำหรับ "ไม่อนุมัติ" คำขอรับรองเวลา
  const [corrRejectId, setCorrRejectId] = useState<string | null>(null);
  const [corrRejectReason, setCorrRejectReason] = useState('');

  const KIND_LABEL_C: Record<string, string> = { morning_in: 'เข้างาน (เช้า)', evening_out: 'เลิกงาน (เย็น)' };
  async function approveCorrection(id: string) {
    try {
      await decideCorrection.mutateAsync({ id, decision: 'approved' });
      toast.success('รับรองเวลาแล้ว — บันทึกลงเวลาเข้างานเรียบร้อย');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }
  async function submitCorrReject() {
    if (!corrRejectId || !corrRejectReason.trim()) { toast.error('ระบุเหตุผลที่ไม่อนุมัติ'); return; }
    try {
      await decideCorrection.mutateAsync({ id: corrRejectId, decision: 'rejected', rejection_reason: corrRejectReason.trim() });
      setCorrRejectId(null); setCorrRejectReason('');
      toast.success('ไม่อนุมัติคำขอแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function approveChange(id: string) {
    try {
      await decideChange.mutateAsync({ id, decision: 'approved' });
      toast.success('อนุมัติแล้ว — ปรับตารางเวรเรียบร้อย');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function submitChangeReject() {
    if (!changeRejectId || !changeRejectReason.trim()) {
      toast.error('ระบุเหตุผลที่ไม่อนุมัติ');
      return;
    }
    try {
      await decideChange.mutateAsync({ id: changeRejectId, decision: 'rejected', rejection_reason: changeRejectReason.trim() });
      setChangeRejectId(null);
      setChangeRejectReason('');
      toast.success('ไม่อนุมัติคำขอแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  function fmtChangeDate(iso: string): string {
    const d = new Date(`${iso}T00:00:00+07:00`);
    // ต้องมี year ด้วย — ไม่งั้นหน้าอนุมัติสลับกะ/ย้ายวันหยุดโชว์แค่วัน-เดือน เห็นปีผิดไม่ได้ (HR แจ้ง)
    return new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Bangkok' }).format(d);
  }

  async function approve(kind: 'leave' | 'ot', id: string) {
    try {
      await act.mutateAsync({ kind, id, action: 'approve' });
      toast.success('อนุมัติแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  function openReason(kind: 'leave' | 'ot', id: string, action: 'reject' | 'request_info') {
    setReasonModal({ kind, id, action });
    setReasonText('');
  }

  async function submitReason() {
    if (!reasonModal || !reasonText.trim()) {
      toast.error(reasonModal?.action === 'reject' ? 'ระบุเหตุผลที่ปฏิเสธ' : 'ระบุข้อความที่ต้องการสอบถาม');
      return;
    }
    const { kind, id, action } = reasonModal;
    try {
      await act.mutateAsync(
        action === 'reject'
          ? { kind, id, action: 'reject', reason: reasonText.trim() }
          : { kind, id, action: 'request_info', info_message: reasonText.trim() },
      );
      setReasonModal(null);
      toast.success(action === 'reject' ? 'ปฏิเสธคำขอแล้ว' : 'ส่งคำขอข้อมูลเพิ่มเติมแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  if (isLoading) return <p>กำลังโหลด…</p>;

  return (
    <>
      <div className="flex gap-2 mb-3">
        <button onClick={() => setTab('leave')} className={tab === 'leave' ? 'btn-primary flex-1' : 'btn-secondary flex-1'}>
          ใบลา ({data?.leaves.length ?? 0})
        </button>
        <button onClick={() => setTab('ot')} className={tab === 'ot' ? 'btn-primary flex-1' : 'btn-secondary flex-1'}>
          OT ({data?.ots.length ?? 0})
        </button>
        <button onClick={() => setTab('shift_change')} className={tab === 'shift_change' ? 'btn-primary flex-1' : 'btn-secondary flex-1'}>
          ย้ายวันหยุด/เปลี่ยนกะ ({data?.shift_changes?.length ?? 0})
        </button>
        {canCertify && (
          <button onClick={() => setTab('attendance_correction')} className={tab === 'attendance_correction' ? 'btn-primary flex-1' : 'btn-secondary flex-1'}>
            รับรองเวลา ({corrections?.length ?? 0})
          </button>
        )}
      </div>

      <div className="space-y-2">
        {tab === 'leave' && groupPendingLeaves(data?.leaves ?? []).map((l) => {
          const isOpen = expanded === `l:${l.leave_id}`;
          return (
            <div key={l.leave_id} className="card p-4">
              <div className="flex justify-between mb-2 gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{l.emp_name ?? l.emp_code}</p>
                  <p className="text-xs text-gray-500">{l.emp_code} · {l.leave_id}</p>
                  <p className="text-sm text-gray-700 mt-1">
                    {LEAVE_TYPE_LABEL[l.leave_type] ?? l.leave_type}
                    {l.period && l.period !== 'full' && ` · ${PERIOD_LABEL[l.period]}`}
                    {' · '}
                    {l.date_label}
                  </p>
                </div>
                <span className="badge-info shrink-0 self-start">L{l.current_level}</span>
              </div>

              <div className="flex gap-1 flex-wrap mb-2">
                {l.is_emergency && (
                  <span className="badge bg-red-100 text-red-700 flex items-center gap-0.5">
                    <AlertTriangle className="w-3 h-3" /> ลาฉุกเฉิน
                  </span>
                )}
                {l.is_backdated && <span className="badge-warn">ลาย้อนหลัง</span>}
              </div>

              <ApprovalTrail timeline={l.approval_timeline} />

              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : `l:${l.leave_id}`)}
                className="text-sm text-brand-700 mb-3 flex items-center gap-1 hover:underline"
              >
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {isOpen ? 'ซ่อนรายละเอียด' : 'ดูรายละเอียด'}
              </button>

              {isOpen && (
                <div className="rounded-lg bg-gray-50 p-3 mb-3 space-y-2 text-sm">
                  <div>
                    <div className="text-xs text-gray-500">เหตุผล</div>
                    <div className="text-gray-900 whitespace-pre-line">{l.reason || '— ไม่ระบุ —'}</div>
                  </div>
                  {l.period === 'custom' && (l.start_time || l.end_time) && (
                    <div>
                      <div className="text-xs text-gray-500">เวลาที่ลา</div>
                      <div>{l.start_time ?? '?'} – {l.end_time ?? '?'}</div>
                    </div>
                  )}
                  {l.evidence_url && (
                    <div>
                      <a
                        href={resolveFileUrl(l.evidence_url) ?? '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="text-brand-700 flex items-center gap-1 hover:underline"
                      >
                        <Paperclip className="w-4 h-4" /> ดูหลักฐานแนบ
                      </a>
                    </div>
                  )}
                  {l.submitted_at && (
                    <div className="text-xs text-gray-500">
                      ส่งเมื่อ {new Date(l.submitted_at).toLocaleString('th-TH')}
                    </div>
                  )}
                </div>
              )}

              {leaveAlerts(l).length > 0 && (
                <div className="mb-3">
                  <ComplianceAlertList items={leaveAlerts(l)} />
                </div>
              )}

              <div className="flex gap-2">
                <button onClick={() => approve('leave', l.leave_id)} className="btn-cta flex-1">
                  <Check className="w-4 h-4" /> อนุมัติ
                </button>
                <button onClick={() => openReason('leave', l.leave_id, 'request_info')} className="btn-secondary" title="ขอข้อมูลเพิ่ม">
                  <MessageCircle className="w-4 h-4" />
                </button>
                <button onClick={() => openReason('leave', l.leave_id, 'reject')} className="btn-danger" title="ปฏิเสธ">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}

        {tab === 'ot' && (data?.ots ?? []).map((o) => {
          const isOpen = expanded === `o:${o.ot_id}`;
          return (
            <div key={o.ot_id} className="card p-4">
              <div className="flex justify-between mb-2 gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{o.emp_name ?? o.emp_code}</p>
                  <p className="text-xs text-gray-500">{o.emp_code} · {o.ot_id}</p>
                  <p className="text-sm text-gray-700 mt-1">
                    {o.ot_type} · {o.date} · {o.start_time}–{o.end_time}
                  </p>
                </div>
                <span className="badge-info shrink-0 self-start">L{o.current_level}</span>
              </div>

              <ApprovalTrail timeline={o.approval_timeline} />

              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : `o:${o.ot_id}`)}
                className="text-sm text-brand-700 mb-3 flex items-center gap-1 hover:underline"
              >
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {isOpen ? 'ซ่อนรายละเอียด' : 'ดูรายละเอียด'}
              </button>

              {isOpen && (
                <div className="rounded-lg bg-gray-50 p-3 mb-3 space-y-2 text-sm">
                  <div>
                    <div className="text-xs text-gray-500">เหตุผล</div>
                    <div className="text-gray-900 whitespace-pre-line">{o.reason || '—'}</div>
                  </div>
                  {o.submitted_at && (
                    <div className="text-xs text-gray-500">
                      ส่งเมื่อ {new Date(o.submitted_at).toLocaleString('th-TH')}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <button onClick={() => approve('ot', o.ot_id)} className="btn-cta flex-1">
                  <Check className="w-4 h-4" /> อนุมัติ
                </button>
                <button onClick={() => openReason('ot', o.ot_id, 'request_info')} className="btn-secondary" title="ขอข้อมูลเพิ่ม">
                  <MessageCircle className="w-4 h-4" />
                </button>
                <button onClick={() => openReason('ot', o.ot_id, 'reject')} className="btn-danger" title="ปฏิเสธ">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}

        {tab === 'shift_change' && (data?.shift_changes ?? []).map((c) => (
          <div key={c.id} className="card p-4">
            <div className="flex justify-between mb-2 gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate flex items-center gap-1.5">
                  {c.emp_name ?? c.emp_code}
                  <span className="badge-info shrink-0">{c.request_type === 'shift_change' ? 'เปลี่ยนกะ' : 'ย้ายวันหยุด'}</span>
                  {c.is_urgent && <span className="badge-warn">กระทันหัน</span>}
                  {(c.required_levels ?? 1) > 1 && (
                    <span className="badge-info shrink-0">
                      ชั้น {c.current_level ?? 1}/{c.required_levels}
                      {(c.current_level ?? 1) >= 2 ? ' · ผ่านหัวหน้าแล้ว' : ''}
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500">{c.emp_code}</p>
                {c.request_type === 'shift_change' ? (
                  <>
                    <p className="text-sm text-gray-700 mt-1">วันที่ {fmtChangeDate(c.original_date)}</p>
                    <p className="text-sm text-gray-700 mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <span>{shiftNameOf(c.original_shift_id) ?? 'กะเดิม'}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                      <span className="font-medium text-ink">{shiftNameOf(c.target_shift_id) ?? 'กะใหม่'}</span>
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">เปลี่ยนกะการทำงานของวันนี้</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-700 mt-1 flex items-center gap-1.5 flex-wrap">
                      <span>หยุด {fmtChangeDate(c.original_date)}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                      <span>หยุด {fmtChangeDate(c.target_date)}</span>
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">วันหยุดเดิมกลับมาทำงาน · วันใหม่หยุดแทน</p>
                  </>
                )}
                {c.submitted_at && (
                  <div className="text-xs text-gray-500 mt-1">
                    ส่งเมื่อ {new Date(c.submitted_at).toLocaleString('th-TH')}
                  </div>
                )}
              </div>
            </div>
            {c.reason && (
              <p className="text-sm text-gray-700 mb-2 p-2 bg-gray-50 rounded">เหตุผล: {c.reason}</p>
            )}
            <div className="flex gap-2">
              <button onClick={() => approveChange(c.id)} disabled={decideChange.isPending} className="btn-cta flex-1">
                <Check className="w-4 h-4" /> อนุมัติ
              </button>
              <button onClick={() => { setChangeRejectId(c.id); setChangeRejectReason(''); }} className="btn-danger" title="ไม่อนุมัติ">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}

        {tab === 'shift_change' && (data?.shift_changes?.length ?? 0) === 0 && (
          <EmptyState
            icon={CheckCircle2}
            title="ไม่มีคำขอย้ายวันหยุด"
            detail="คำขอย้ายวันหยุดของพนักงานจะมาแสดงที่นี่"
          />
        )}

        {tab === 'attendance_correction' && (corrections ?? []).map((c) => (
          <div key={c.id} className="card p-4">
            <div className="flex justify-between mb-2 gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{c.emp_name ?? c.emp_code}</p>
                <p className="text-xs text-gray-500">{c.emp_code}</p>
                <p className="text-sm text-gray-700 mt-1">
                  {fmtChangeDate(c.date)} · {KIND_LABEL_C[c.kind] ?? c.kind} · ขอรับรองเวลา <b>{c.requested_time} น.</b>
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-700 mb-2 p-2 bg-gray-50 rounded">เหตุผล: {c.reason}</p>
            <div className="flex gap-2">
              <button onClick={() => approveCorrection(c.id)} disabled={decideCorrection.isPending} className="btn-cta flex-1">
                <Check className="w-4 h-4" /> รับรองเวลา
              </button>
              <button onClick={() => { setCorrRejectId(c.id); setCorrRejectReason(''); }} className="btn-danger" title="ไม่อนุมัติ">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}

        {tab === 'attendance_correction' && (corrections?.length ?? 0) === 0 && (
          <EmptyState
            icon={CheckCircle2}
            title="ไม่มีคำขอรับรองเวลา"
            detail="คำขอรับรองเวลาเข้า-ออกงานของพนักงานจะมาแสดงที่นี่"
          />
        )}

        {tab !== 'shift_change' && tab !== 'attendance_correction' && (data?.leaves.length === 0 && data?.ots.length === 0) && (
          <EmptyState
            icon={CheckCircle2}
            title="ไม่มีคำขอที่รออนุมัติ"
            detail="เคลียร์หมดแล้ว — คำขอลา/OT ใหม่จะมาแสดงที่นี่"
          />
        )}
      </div>

      <Modal
        open={reasonModal !== null}
        onClose={() => setReasonModal(null)}
        title={reasonModal?.action === 'reject' ? 'ปฏิเสธคำขอ' : 'ขอข้อมูลเพิ่มเติม'}
      >
        <div className="space-y-3">
          <div>
            <label className="label">{reasonModal?.action === 'reject' ? 'เหตุผลที่ปฏิเสธ' : 'ข้อความถึงพนักงาน'}</label>
            <textarea className="input" rows={3} value={reasonText} onChange={(e) => setReasonText(e.target.value)} placeholder="พิมพ์ข้อความ…" />
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setReasonModal(null)}>ยกเลิก</button>
            <button
              className={`flex-1 ${reasonModal?.action === 'reject' ? 'btn-danger' : 'btn-primary'}`}
              onClick={submitReason}
              disabled={act.isPending}
            >
              {reasonModal?.action === 'reject' ? 'ปฏิเสธ' : 'ส่งคำขอ'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={changeRejectId !== null}
        onClose={() => setChangeRejectId(null)}
        title="ไม่อนุมัติคำขอย้ายวันหยุด"
      >
        <div className="space-y-3">
          <div>
            <label className="label">เหตุผลที่ไม่อนุมัติ</label>
            <textarea className="input" rows={3} value={changeRejectReason} onChange={(e) => setChangeRejectReason(e.target.value)} placeholder="พิมพ์เหตุผล…" maxLength={300} />
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setChangeRejectId(null)}>ยกเลิก</button>
            <button className="btn-danger flex-1" onClick={submitChangeReject} disabled={decideChange.isPending}>
              ไม่อนุมัติ
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={corrRejectId !== null}
        onClose={() => setCorrRejectId(null)}
        title="ไม่อนุมัติคำขอรับรองเวลา"
      >
        <div className="space-y-3">
          <div>
            <label className="label">เหตุผลที่ไม่อนุมัติ</label>
            <textarea className="input" rows={3} value={corrRejectReason} onChange={(e) => setCorrRejectReason(e.target.value)} placeholder="พิมพ์เหตุผล…" maxLength={300} />
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setCorrRejectId(null)}>ยกเลิก</button>
            <button className="btn-danger flex-1" onClick={submitCorrReject} disabled={decideCorrection.isPending}>
              ไม่อนุมัติ
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

/**
 * แท็บ "ยกเลิกที่อนุมัติแล้ว" — owner/HR ดูใบลา/OT ที่อนุมัติแล้ว แต่ยังมาไม่ถึง
 * แล้วขอยกเลิก (ต้องระบุเหตุผล) → ระบบส่งให้พนักงานกดยินยอมก่อนถึงจะยกเลิกจริง.
 */
function fmtThaiDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00+07:00`);
  return new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Bangkok' }).format(d);
}

function UpcomingApprovedContent() {
  const { data, isLoading } = useUpcomingApproved();
  const requestCancel = useRequestCancelApproved();
  const toast = useToast();
  const [modal, setModal] = useState<{ kind: 'leave' | 'ot'; record_id: string; who: string; what: string } | null>(null);
  const [reason, setReason] = useState('');

  const submit = async () => {
    if (!modal) return;
    if (reason.trim().length === 0) { toast.error('กรุณาระบุเหตุผล'); return; }
    try {
      await requestCancel.mutateAsync({ kind: modal.kind, record_id: modal.record_id, reason: reason.trim() });
      toast.success('ส่งคำขอยกเลิกแล้ว — รอพนักงานยินยอม');
      setModal(null);
      setReason('');
    } catch (e: any) {
      toast.error(e?.message ?? 'ส่งคำขอไม่สำเร็จ');
    }
  };

  if (isLoading) return <div className="text-center text-gray-400 py-10 text-sm">กำลังโหลด…</div>;

  const leaves = data?.leaves ?? [];
  const ots = data?.ots ?? [];
  if (leaves.length === 0 && ots.length === 0) {
    return <EmptyState title="ไม่มีใบลา/OT ที่อนุมัติแล้วและยังมาไม่ถึง" detail="ยกเลิกได้เฉพาะรายการที่อนุมัติแล้ว แต่วันยังไม่มาถึงเท่านั้น" />;
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">ยกเลิกได้เฉพาะรายการที่ <strong>ยังมาไม่ถึง</strong> และต้องให้พนักงาน <strong>กดยินยอม</strong> ก่อนจึงจะยกเลิกจริง</p>

      {leaves.map((l) => (
        <div key={l.record_id} className="card p-3">
          <div className="flex justify-between items-start">
            <div className="min-w-0">
              <p className="font-medium">{l.emp_name}</p>
              <p className="text-sm text-gray-700">{l.type_label}{l.days_count > 1 ? ` ${l.days_count} วัน` : ''}</p>
              <p className="text-sm text-gray-500">
                {l.days_count > 1 ? `${fmtThaiDate(l.date)} – ${fmtThaiDate(l.end_date)}` : fmtThaiDate(l.date)}
              </p>
              {l.reason && <p className="text-sm text-gray-600 mt-1 line-clamp-2">"{l.reason}"</p>}
            </div>
          </div>
          {l.cancel_pending ? (
            <p className="text-xs text-amber-600 mt-2 p-2 bg-amber-50 rounded">⏳ รอพนักงานยินยอมการยกเลิก{l.cancel_reason ? ` · ${l.cancel_reason}` : ''}</p>
          ) : (
            <button onClick={() => setModal({ kind: 'leave', record_id: l.record_id, who: l.emp_name, what: `${l.type_label} ${fmtThaiDate(l.date)}` })}
              className="btn-secondary w-full mt-2 text-sm text-red-600 border-red-200 hover:bg-red-50">ขอยกเลิกการลานี้</button>
          )}
        </div>
      ))}

      {ots.map((o) => (
        <div key={o.record_id} className="card p-3">
          <div className="min-w-0">
            <p className="font-medium">{o.emp_name}</p>
            <p className="text-sm text-gray-700">{o.type_label}{o.start_time && o.end_time ? ` ${o.start_time}-${o.end_time}` : ''}</p>
            <p className="text-sm text-gray-500">{fmtThaiDate(o.date)}</p>
            {o.reason && <p className="text-sm text-gray-600 mt-1 line-clamp-2">"{o.reason}"</p>}
          </div>
          {o.cancel_pending ? (
            <p className="text-xs text-amber-600 mt-2 p-2 bg-amber-50 rounded">⏳ รอพนักงานยินยอมการยกเลิก{o.cancel_reason ? ` · ${o.cancel_reason}` : ''}</p>
          ) : (
            <button onClick={() => setModal({ kind: 'ot', record_id: o.record_id, who: o.emp_name, what: `${o.type_label} ${fmtThaiDate(o.date)}` })}
              className="btn-secondary w-full mt-2 text-sm text-red-600 border-red-200 hover:bg-red-50">ขอยกเลิก OT นี้</button>
          )}
        </div>
      ))}

      <Modal open={!!modal} onClose={() => { setModal(null); setReason(''); }} title="ขอยกเลิกรายการที่อนุมัติแล้ว">
        {modal && (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">{modal.who} · {modal.what}</p>
            <p className="text-xs text-gray-500">พนักงานจะได้รับแจ้งและต้องกดยินยอมก่อน รายการถึงจะถูกยกเลิก</p>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500}
              placeholder="เหตุผลในการขอยกเลิก (เช่น มีงานด่วน ต้องให้มาทำงาน)" className="input w-full" />
            <button onClick={submit} disabled={requestCancel.isPending} className="btn-primary w-full justify-center disabled:opacity-50">
              {requestCancel.isPending ? 'กำลังส่ง…' : 'ส่งคำขอยกเลิก'}
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}

// Status badge for decided history rows — mirrors StatusBadge style in MyRequests.
const HIST_BADGE = 'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full';
function HistoryStatusBadge({ status }: { status: 'approved' | 'rejected' }) {
  if (status === 'approved')
    return <span className={`${HIST_BADGE} bg-emerald-100 text-emerald-700`}><Check className="w-3 h-3" /> อนุมัติแล้ว</span>;
  return <span className={`${HIST_BADGE} bg-red-100 text-red-700`}><X className="w-3 h-3" /> ปฏิเสธ</span>;
}

/**
 * History tab — read-only log of decided leave/OT/shift requests (HR/OWNER).
 * Server-side filters: date range, status, type. Employee name/code is filtered
 * client-side for v1 (no employee-picker dependency here).
 */
function ApprovalHistoryContent() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState<'all' | 'approved' | 'rejected'>('all');
  const [type, setType] = useState<'all' | 'leave' | 'ot' | 'shift'>('all');
  const [empFilter, setEmpFilter] = useState('');

  const { data, isLoading } = useApprovalHistory({
    from: from || undefined,
    to: to || undefined,
    status,
    type,
  });

  const items: ApprovalHistoryItem[] = data?.items ?? [];
  const q = empFilter.trim().toLowerCase();
  const filtered = q
    ? items.filter(
        (it) =>
          it.emp_name.toLowerCase().includes(q) || it.emp_code.toLowerCase().includes(q),
      )
    : items;

  return (
    <div className="space-y-3">
      <div className="card p-3 space-y-2">
        <div className="flex gap-2">
          <label className="flex-1 text-xs text-gray-500">
            ตั้งแต่
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input w-full mt-1" />
          </label>
          <label className="flex-1 text-xs text-gray-500">
            ถึง
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input w-full mt-1" />
          </label>
        </div>
        <div className="flex gap-2">
          <label className="flex-1 text-xs text-gray-500">
            สถานะ
            <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} className="input w-full mt-1">
              <option value="all">ทั้งหมด</option>
              <option value="approved">อนุมัติ</option>
              <option value="rejected">ปฏิเสธ</option>
            </select>
          </label>
          <label className="flex-1 text-xs text-gray-500">
            ประเภท
            <select value={type} onChange={(e) => setType(e.target.value as typeof type)} className="input w-full mt-1">
              <option value="all">ทั้งหมด</option>
              <option value="leave">ลา</option>
              <option value="ot">OT</option>
              <option value="shift">ย้ายวันหยุด</option>
            </select>
          </label>
        </div>
        <label className="block text-xs text-gray-500">
          พนักงาน
          <input
            type="text"
            value={empFilter}
            onChange={(e) => setEmpFilter(e.target.value)}
            placeholder="ชื่อ หรือ รหัสพนักงาน"
            className="input w-full mt-1"
          />
        </label>
      </div>

      {isLoading ? (
        <p className="text-center text-gray-400 py-10 text-sm">กำลังโหลด…</p>
      ) : filtered.length === 0 ? (
        <EmptyState title="ไม่มีประวัติ" detail="ลองปรับช่วงวันที่หรือตัวกรอง" />
      ) : (
        <div className="space-y-2">
          {filtered.map((it) => (
            <div key={`${it.kind}-${it.id}`} className="card p-3">
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                  <p className="font-medium">
                    {it.emp_name}
                    <span className="text-xs text-gray-400 font-normal"> · {it.emp_code}</span>
                  </p>
                  <p className="text-sm text-gray-700">
                    {it.type_label}
                    {it.is_emergency ? <span className="text-red-600"> (ฉุกเฉิน)</span> : ''}
                  </p>
                  <p className="text-sm text-gray-500">{fmtThaiDate(it.date)}</p>
                </div>
                <HistoryStatusBadge status={it.status} />
              </div>
              {it.reason && <p className="text-sm text-gray-600 mt-1 line-clamp-2">"{it.reason}"</p>}
              {it.decided_at && (
                <p className="text-xs text-gray-400 mt-1">ตัดสินเมื่อ {fmtThaiDate(it.decided_at.slice(0, 10))}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Top-level Approvals hub. Renders tabs (filtered by role) and delegates
 * to the matching content component. Keeps URL hash in sync so deep links
 * like /approvals#emergency from briefing/close-readiness still work.
 */
export function Approvals() {
  const role = useAuthStore((s) => s.profile?.role ?? 'EMPLOYEE');
  const isAdmin = role === 'OWNER' || role === 'HR' || role === 'MANAGER';
  const badges = useMenuBadgeCounts(role);

  const [topTab, setTopTab] = useState<TopTab>(() => {
    const h = (typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '') as TopTab;
    return VALID_TOP_TABS.includes(h) ? h : 'leave-ot';
  });

  // Keep the URL hash aligned with the active tab (replaceState avoids
  // polluting browser history when switching tabs).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const next = `#${topTab}`;
    if (window.location.hash !== next) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + next);
    }
  }, [topTab]);

  const canCancelApproved = role === 'OWNER' || role === 'HR';
  const canViewHistory = role === 'OWNER' || role === 'HR';
  const tabs: Array<{ key: TopTab; label: string; count: number; visible: boolean }> = [
    { key: 'leave-ot', label: 'ใบลา / OT', count: badges.approvals, visible: true },
    { key: 'upcoming', label: 'ยกเลิกที่อนุมัติแล้ว', count: 0, visible: canCancelApproved },
    { key: 'emergency', label: 'ลาฉุกเฉิน', count: badges.emergency, visible: isAdmin },
    { key: 'changes', label: 'คำขอแก้ข้อมูล', count: badges.pendingChanges, visible: isAdmin },
    { key: 'tickets', label: 'ร้องเรียน', count: badges.tickets, visible: isAdmin },
    { key: 'history', label: 'ประวัติ', count: 0, visible: canViewHistory },
  ];
  const visibleTabs = tabs.filter((t) => t.visible);
  const activeTab = visibleTabs.some((t) => t.key === topTab) ? topTab : 'leave-ot';

  return (
    <AdminLayout title="อนุมัติคำขอ">
      <HubHeader
        section="เวลาทำงาน · ลา · OT"
        title={visibleTabs.find((t) => t.key === activeTab)?.label ?? 'อนุมัติคำขอ'}
        roleLabel={roleLabelTh(role)}
      />
      <PillTabs
        tabs={visibleTabs}
        active={activeTab}
        onChange={setTopTab}
        badges={Object.fromEntries(visibleTabs.map((t) => [t.key, t.count])) as Partial<Record<TopTab, number>>}
      />

      {activeTab === 'leave-ot' && <LeaveOtApprovalsContent />}
      {activeTab === 'upcoming' && <UpcomingApprovedContent />}
      {activeTab === 'emergency' && <HREmergencyLeaveContent />}
      {activeTab === 'changes' && <HRPendingContent />}
      {activeTab === 'tickets' && <HRTicketsContent />}
      {activeTab === 'history' && <ApprovalHistoryContent />}
    </AdminLayout>
  );
}
